import { Card, CardContent } from '@/components/ui/card';
import { Heart, Eye, CalendarCheck, TrendingUp, Bookmark, Globe } from 'lucide-react';
import type { DashboardMetrics, TrafficMetrics } from '@/hooks/useProducerDashboardMetrics';
import { Skeleton } from '@/components/ui/skeleton';

interface DashboardCardsProps {
  dbMetrics: DashboardMetrics | null;
  trafficMetrics: TrafficMetrics | null;
  isLoading: boolean;
}

function MetricCard({
  icon: Icon,
  label,
  value,
  isLoading,
}: {
  icon: React.ElementType;
  label: string;
  value: number | string | null;
  isLoading: boolean;
}) {
  return (
    <Card className="border-border bg-card">
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-md bg-muted">
            <Icon className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="min-w-0">
            <p className="text-xs text-muted-foreground truncate">{label}</p>
            {isLoading ? (
              <Skeleton className="h-6 w-12 mt-0.5" />
            ) : (
              <p className="text-lg font-medium text-foreground tabular-nums">
                {value ?? '–'}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function DashboardCards({ dbMetrics, trafficMetrics, isLoading }: DashboardCardsProps) {
  return (
    <div className="space-y-6">
      {/* Traffic */}
      <section>
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">Trafik</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard
            icon={Eye}
            label="Sidvisningar"
            value={trafficMetrics?.producer_page_views ?? null}
            isLoading={isLoading}
          />
          <MetricCard
            icon={CalendarCheck}
            label="Aktivitetsvisningar"
            value={trafficMetrics?.activity_views ?? null}
            isLoading={isLoading}
          />
          <MetricCard
            icon={TrendingUp}
            label="Bokningar startade"
            value={trafficMetrics?.booking_started ?? null}
            isLoading={isLoading}
          />
          <MetricCard
            icon={CalendarCheck}
            label="Bokningar klara"
            value={trafficMetrics?.booking_completed ?? null}
            isLoading={isLoading}
          />
        </div>
      </section>

      {/* Saved */}
      <section>
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">Sparat av besökare</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <MetricCard
            icon={Heart}
            label="Sparat er gård"
            value={dbMetrics?.saved_producer_count ?? null}
            isLoading={isLoading}
          />
          <MetricCard
            icon={Bookmark}
            label="Sparat aktiviteter"
            value={dbMetrics?.saved_activity_count ?? null}
            isLoading={isLoading}
          />
          {dbMetrics?.has_place && (
            <MetricCard
              icon={Bookmark}
              label="Sparat plats"
              value={dbMetrics?.saved_place_count ?? null}
              isLoading={isLoading}
            />
          )}
        </div>
      </section>

      {/* Highlights */}
      {(dbMetrics?.most_saved_activity || trafficMetrics?.top_utm_source) && (
        <section>
          <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">Det som funkar</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {dbMetrics?.most_saved_activity && (
              <Card className="border-border bg-card">
                <CardContent className="p-4">
                  <p className="text-xs text-muted-foreground">Mest sparade aktivitet</p>
                  <p className="text-sm font-medium text-foreground mt-1 truncate">
                    {dbMetrics.most_saved_activity.title}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {dbMetrics.most_saved_activity.count} sparade
                  </p>
                </CardContent>
              </Card>
            )}
            {trafficMetrics?.top_utm_source && (
              <Card className="border-border bg-card">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2">
                    <Globe className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-xs text-muted-foreground">Största trafikkälla</p>
                      <p className="text-sm font-medium text-foreground mt-0.5">
                        {trafficMetrics.top_utm_source}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
